import React, { useCallback, useEffect, useState } from 'react';
import { fetchLeads, deleteLead, updateLead, bulkDeleteLeads } from '../services/leadService';
import { getAllUsers } from '../services/userServices';
import BulkImportModal from '../components/BulkImportModal';
import LeadsTable from '../components/LeadsTable';
import NotificationSnackbar from '../components/ui/NotificationSnackbar';
import { useSettings } from '../context/SettingsContext';
import useAutoRefresh from '../hooks/useAutoRefresh';

const Leads = () => {
    const { settings } = useSettings();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    const showNotification = useCallback((message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    }, []);

    const handleCloseNotification = useCallback(() => {
        setNotification((prev) => ({ ...prev, open: false }));
    }, []);

    const getLeadsSignature = useCallback((items) => {
        if (!Array.isArray(items)) return '[]';

        return JSON.stringify(
            items.map((item) => ({
                id: Number(item?.id || 0),
                updated_at: String(item?.updated_at || ''),
                status: String(item?.status || ''),
                lead_status: String(item?.lead_status || ''),
                priority: String(item?.priority || ''),
                first_name: String(item?.first_name || ''),
                last_name: String(item?.last_name || ''),
                company: String(item?.company_name || item?.company || ''),
                email: String(item?.email || ''),
                phone: String(item?.phone_number || ''),
                owner: String(item?.assigned_salesperson || ''),
            }))
        );
    }, []);

    const leadStatusOptions = ['new', 'in-progress', 'closed', 'won', 'lost'];
    const priorityOptions = ['low', 'medium', 'high'];
    const visibleFields = [];
    const fieldOrderLoaded = true;
    const [users, setUsers] = useState([]);

    const isGeneralBusiness = String(settings?.business_type || 'GENERAL').toUpperCase() === 'GENERAL';
    const eventFields = ['event_name', 'event_type', 'event_date', 'event_time', 'event_location', 'venue'];
    const filteredVisibleFields = isGeneralBusiness
        ? visibleFields.filter((field) => !eventFields.includes(String(field || '').toLowerCase()))
        : visibleFields;


    useEffect(() => {
        const fetchOwners = async () => {
            try {
                const response = await getAllUsers();
                setUsers(Array.isArray(response) ? response : response?.users || []);
            } catch (error) {
                console.error('Failed to load contact owners', error);
            }
        };

        fetchOwners();
    }, []);

    const getLeads = useCallback(async ({ silent = false } = {}) => {
        if (!silent) {
            setLoading(true);
        }

        try {
            setLoadError('');
            const response = await fetchLeads();
            const nextLeads = Array.isArray(response?.leads) ? response.leads : [];

            setLeads((prev) => {
                const prevSignature = getLeadsSignature(prev);
                const nextSignature = getLeadsSignature(nextLeads);
                return prevSignature === nextSignature ? prev : nextLeads;
            });
        } catch (error) {
            console.error(error);
            setLoadError(error?.response?.data?.error || 'Please check your connection and try again.');
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, [getLeadsSignature]);

    useEffect(() => {
        getLeads({ silent: false });
    }, [getLeads]);

    const handleLeadsAutoRefresh = useCallback(() => {
        return getLeads({ silent: true });
    }, [getLeads]);

    useAutoRefresh(handleLeadsAutoRefresh, { intervalMs: 20000 });

    const handleBulkDelete = async (leadIds = []) => {
        const ids = Array.isArray(leadIds) ? leadIds : [];
        if (!ids.length) return;

        try {
            const result = await bulkDeleteLeads(ids);
            await getLeads();

            const deletedCount = Number(result?.deleted_count || 0);
            const notFoundIds = Array.isArray(result?.not_found_ids) ? result.not_found_ids : [];

            if (deletedCount > 0) {
                const baseMessage = `${deletedCount} contact${deletedCount === 1 ? '' : 's'} deleted successfully`;
                const message = notFoundIds.length
                    ? `${baseMessage}. Not found: ${notFoundIds.join(', ')}`
                    : baseMessage;

                showNotification(message, notFoundIds.length ? 'warning' : 'success');
            } else {
                showNotification('No contacts were deleted', 'warning');
            }
        } catch (error) {
            console.error(error);
            showNotification(error?.response?.data?.error || 'Failed to delete selected contacts', 'error');
            throw error;
        }
    };

    const handleDeleteContact = async (contactId) => {
        try {
            await deleteLead(contactId);
            await getLeads({ silent: true });
            showNotification('Contact deleted successfully', 'success');
        } catch (error) {
            console.error(error);
            showNotification(error?.response?.data?.error || 'Failed to delete contact', 'error');
            throw error;
        }
    };

    const [importModalOpen, setImportModalOpen] = useState(false);

    const handleImportComplete = (err, result) => {
        if (err) {
            console.error('Bulk import error', err);
            showNotification('Bulk import failed', 'error');
            return;
        }

        if (result?.failed > 0) {
            showNotification(`Imported ${result.success}/${result.total} contacts. ${result.failed} failed.`, 'warning');
            console.table(result.errors || []);
        } else {
            showNotification(`Successfully imported ${result.success} contacts`, 'success');
        }

        // refresh list after a successful import
        getLeads();
    };

    const handleUpdateLead = async (contactId, patch) => {
        try {
            await updateLead(contactId, patch);
            await getLeads({ silent: true });
        } catch (error) {
            console.error(error);
            showNotification(error?.response?.data?.error || 'Failed to update contact', 'error');
            throw error;
        }
    };

    return (
        <div className="contacts-page">
                <LeadsTable
                    key={fieldOrderLoaded ? 'contacts-configured' : 'contacts-loading'}
                    leads={leads}
                    visibleFields={filteredVisibleFields}
                    onBulkDelete={handleBulkDelete}
                    onDeleteContact={handleDeleteContact}
                    leadStatusOptions={leadStatusOptions}
                    priorityOptions={priorityOptions}
                    onUpdateLead={handleUpdateLead}
                    onImportBulk={() => setImportModalOpen(true)}
                    loading={loading}
                    error={loadError}
                    onRetry={() => getLeads({ silent: false })}
                    onRefresh={() => getLeads({ silent: false })}
                    columnConfigReady={fieldOrderLoaded}
                    users={users}
                />

                <NotificationSnackbar
                    open={notification.open}
                    message={notification.message}
                    severity={notification.severity}
                    onClose={handleCloseNotification}
                />
                <BulkImportModal
                    open={importModalOpen}
                    onClose={() => setImportModalOpen(false)}
                    onComplete={handleImportComplete}
                />
        </div>
    );
};

export default Leads;
